/**
 * LetterPool — weighted letter generation with digraph awareness.
 *
 * Uses WeightedRandom for per-tile frequency control (Scrabble-style),
 * then a digraph pass biases adjacent tiles toward common English
 * letter pairs so natural words emerge on the grid.
 */

import { cellKey, Cell } from "../../../../framework/geometry";
import { weightedPick, Weighted } from "../../../../framework/random";
import type { GridMask } from "./GridMask";

/** Letter frequency weights (loosely based on Scrabble tile counts). */
export const LETTER_WEIGHTS: Weighted<string>[] = [
  // Very common (weight 8–12)
  { item: "E", weight: 12 },
  { item: "A", weight: 9 },
  { item: "I", weight: 9 },
  { item: "O", weight: 8 },
  { item: "N", weight: 6 },
  { item: "R", weight: 6 },
  { item: "T", weight: 6 },
  { item: "S", weight: 6 },
  { item: "L", weight: 4 },
  // Common (weight 3–4)
  { item: "D", weight: 4 },
  { item: "U", weight: 4 },
  { item: "G", weight: 3 },
  { item: "C", weight: 3 },
  { item: "M", weight: 3 },
  { item: "P", weight: 3 },
  { item: "H", weight: 3 },
  // Uncommon (weight 2)
  { item: "B", weight: 2 },
  { item: "F", weight: 2 },
  { item: "W", weight: 2 },
  { item: "Y", weight: 2 },
  { item: "V", weight: 2 },
  { item: "K", weight: 2 },
  // Rare (weight 1)
  { item: "J", weight: 1 },
  { item: "X", weight: 1 },
  { item: "Q", weight: 1 },
  { item: "Z", weight: 1 },
];

const VOWELS = new Set(["A", "E", "I", "O", "U"]);
const VOWEL_WEIGHTS: Weighted<string>[] = LETTER_WEIGHTS.filter(w => VOWELS.has(w.item));

/** Minimum fraction of tiles that should be vowels. */
const MIN_VOWEL_RATIO = 0.25;

/**
 * Common English digraphs — for each letter, the partners that commonly
 * follow or precede it. Used to bias adjacent tiles for word-friendliness.
 */
const DIGRAPH_PARTNERS: Record<string, string[]> = {
  T: ["H", "E", "O", "A", "I", "R", "S"],
  H: ["E", "A", "I", "O", "T"],
  S: ["T", "E", "H", "A", "I", "O"],
  E: ["R", "S", "N", "D", "T", "A"],
  R: ["E", "A", "I", "O", "S", "T"],
  I: ["N", "T", "S", "E", "O", "C"],
  N: ["E", "G", "D", "T", "I", "O"],
  A: ["N", "T", "R", "L", "S", "D"],
  O: ["N", "R", "U", "T", "F"],
  L: ["E", "I", "A", "L", "O", "Y"],
  D: ["E", "I", "A", "O"],
  C: ["H", "E", "O", "A", "K"],
  U: ["R", "N", "S", "T", "L"],
  P: ["R", "E", "A", "O", "L"],
  M: ["E", "A", "I", "O"],
  G: ["E", "R", "A", "H", "O"],
  B: ["E", "A", "L", "O", "R"],
  W: ["A", "E", "I", "O", "H"],
  F: ["E", "O", "I", "A", "R"],
  Y: ["E", "O", "A", "S"],
  V: ["E", "I", "A", "O"],
  K: ["E", "I", "N"],
  Q: ["U"],
};

/** Chance that a tile will be biased by an adjacent tile's digraph. */
const DIGRAPH_CHANCE = 0.35;

/** Word lengths to seed (picked randomly each attempt). */
const SEED_LENGTHS = [6, 7, 8, 9, 10];

/** Max consecutive failures before giving up seeding. */
const MAX_SEED_FAILURES = 8;

/** Optional function to pull a random word of a given length. */
export type WordPicker = (len: number) => string | null;

/** Pick a random letter using weights. */
export function randomLetter(): string {
  return weightedPick(LETTER_WEIGHTS) ?? "E";
}

/** Pick a random vowel using vowel-only weights. */
function randomVowel(): string {
  return weightedPick(VOWEL_WEIGHTS) ?? "E";
}

/** Pick a letter biased toward digraph partners of the given letter. */
function digraphBiasedLetter(neighbor: string): string {
  const partners = DIGRAPH_PARTNERS[neighbor];
  if (!partners || partners.length === 0) return randomLetter();
  return partners[Math.floor(Math.random() * partners.length)];
}

/** Get 8-directional neighbors of a cell that exist in the mask. */
function getNeighbors(cell: Cell, mask: GridMask): Cell[] {
  const result: Cell[] = [];
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      if (dr === 0 && dc === 0) continue;
      const n = { row: cell.row + dr, col: cell.col + dc };
      if (mask.has(n)) result.push(n);
    }
  }
  return result;
}

/**
 * Find a path of `word.length` cells on the mask where each cell either:
 *   - is unfilled (will get a new letter), or
 *   - already has the matching letter from a previous seed (reuse)
 *
 * Returns the path or null. A cell is only used once per path.
 */
function findWordPath(
  mask: GridMask,
  word: string,
  letters: Map<string, string>,
  filled: Set<string>,
): Cell[] | null {
  const allCells = mask.toArray();
  // Shuffle starting points
  for (let i = allCells.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [allCells[i], allCells[j]] = [allCells[j], allCells[i]];
  }

  const maxStarts = Math.min(allCells.length, 20);
  for (let s = 0; s < maxStarts; s++) {
    const path = wordDfs(mask, allCells[s], word, 0, letters, filled);
    if (path) return path;
  }
  return null;
}

function wordDfs(
  mask: GridMask,
  cell: Cell,
  word: string,
  idx: number,
  letters: Map<string, string>,
  filled: Set<string>,
): Cell[] | null {
  const key = cellKey(cell);
  const existing = letters.get(key);

  // Cell already has a letter — only reuse if it matches the word's letter at this position
  if (filled.has(key)) {
    if (existing !== word[idx]) return null;
  }

  const visited = new Set<string>();
  const path: Cell[] = [];

  function walk(c: Cell, i: number): boolean {
    const k = cellKey(c);
    if (visited.has(k)) return false;
    const ex = letters.get(k);
    // If cell is filled, only accept if letter matches
    if (filled.has(k) && ex !== word[i]) return false;
    // If cell is unfilled, it will get the word's letter — always ok

    visited.add(k);
    path.push(c);
    if (i === word.length - 1) return true;

    // Get neighbors, shuffled
    const neighbors = getNeighbors(c, mask).filter(n => !visited.has(cellKey(n)));
    for (let j = neighbors.length - 1; j > 0; j--) {
      const r = Math.floor(Math.random() * (j + 1));
      [neighbors[j], neighbors[r]] = [neighbors[r], neighbors[j]];
    }

    for (const n of neighbors) {
      if (walk(n, i + 1)) return true;
    }

    path.pop();
    visited.delete(k);
    return false;
  }

  return walk(cell, idx) ? path : null;
}

/** Pick a random seed length from the available range. */
function randomSeedLength(): number {
  return SEED_LENGTHS[Math.floor(Math.random() * SEED_LENGTHS.length)];
}

export interface GenerateResult {
  letters: Map<string, string>;
  seededWords: string[];
}

/** Generate letters for each cell in a mask. */
export function generateMaskLetters(mask: GridMask, wordPicker?: WordPicker): GenerateResult {
  const cells = mask.toArray();
  const letters = new Map<string, string>();
  const seededCells = new Set<string>();
  const seededWords: string[] = [];

  // --- Phase 1: Seed words until we can't fit any more ---
  // Words can share cells if the letter at that position matches.
  // Keep going until MAX_SEED_FAILURES consecutive fails or all cells filled.
  if (wordPicker && cells.length >= 6) {
    let failures = 0;

    while (failures < MAX_SEED_FAILURES) {
      const len = randomSeedLength();
      const word = wordPicker(len);
      if (!word) { failures++; continue; }

      const path = findWordPath(mask, word, letters, seededCells);
      if (!path) { failures++; continue; }

      // Place the word — cells that already match keep their letter,
      // new cells get written
      for (let i = 0; i < path.length; i++) {
        const key = cellKey(path[i]);
        letters.set(key, word[i]);
        seededCells.add(key);
      }
      seededWords.push(word);
      failures = 0; // reset on success
    }
  }

  // --- Phase 2: Fill remaining cells with digraph-aware random ---
  const unfilled = cells.filter(c => !seededCells.has(cellKey(c)));
  for (let i = unfilled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [unfilled[i], unfilled[j]] = [unfilled[j], unfilled[i]];
  }

  for (const cell of unfilled) {
    const key = cellKey(cell);

    if (Math.random() < DIGRAPH_CHANCE) {
      const neighbors = getNeighbors(cell, mask);
      const placed = neighbors
        .map(n => letters.get(cellKey(n)))
        .filter((l): l is string => l !== undefined);
      if (placed.length > 0) {
        const seed = placed[Math.floor(Math.random() * placed.length)];
        letters.set(key, digraphBiasedLetter(seed));
        continue;
      }
    }

    letters.set(key, randomLetter());
  }

  // --- Phase 3: Guarantee minimum vowel count (skip seeded cells) ---
  const minVowels = Math.ceil(cells.length * MIN_VOWEL_RATIO);
  const consonantKeys: string[] = [];
  let vowelCount = 0;
  for (const [key, letter] of letters) {
    if (VOWELS.has(letter)) vowelCount++;
    else if (!seededCells.has(key)) consonantKeys.push(key);
  }
  while (vowelCount < minVowels && consonantKeys.length > 0) {
    const idx = Math.floor(Math.random() * consonantKeys.length);
    const key = consonantKeys.splice(idx, 1)[0];
    letters.set(key, randomVowel());
    vowelCount++;
  }

  return { letters, seededWords };
}
